import {gql} from "@apollo/client";

import {AlertExtraData, GlobalAlertState} from "@/lib/types/alerting.ts";
import {ServiceItems, ServiceTag, StepDefinitions} from "@/lib/types/checks.ts";
import {ContactGroupBaseItems} from "@/lib/types/contacts.ts";
import {MonitoringGroup} from "@/lib/types/monitoring.ts";

export interface GlobalAlertStateStatsResponse {
  resolved: number;
  down: number;
  ignored: number;
  sms_and_phone_alerts_used: number;
  sms_and_phone_alerts_available: number;
}

export interface GlobalAlertStatesResponse {
  stats: GlobalAlertStateStatsResponse;
  items: GlobalAlertState[];
  total: number;
}

export interface CheckGlobalAlertsResponse {
  globalAlertStates: GlobalAlertStatesResponse;
}

export const GQL_CHECK_GLOBAL_ALERTS = gql`
  query globalAlertStates($serviceId: Int!, $offset: Int!, $limit: Int!) {
    globalAlertStates(serviceId: $serviceId, offset: $offset, limit: $limit) {
      items {
        ...GlobalAlertStateFields
        alert {
          id
          created_at
          monitoring_server {
            id
            name
            location
          }
          event_id
          alerted_at
          state
          type
          attempt_number
          output
        }
      }
      total
    }
  }
`;

export interface CheckGlobalAlertResponse {
  globalAlertState: GlobalAlertState;
  stepsDef: StepDefinitions;
}

export const GQL_CHECK_GLOBAL_ALERT = gql`
  query globalAlertState($id: Int!) {
    globalAlertState(id: $id) {
      ...GlobalAlertStateFields
      alert {
        id
        created_at
        monitoring_server {
          id
          name
          location
          address_ipv4
          address_ipv6
        }
        event_id
        alerted_at
        state
        type
        attempt_number
        output
      }
      service {
        id
      }
      down_alerts {
        id
        created_at
        monitoring_server {
          id
          name
          location
          address_ipv4
          address_ipv6
        }
        created_at
        output
      }
    }
    stepsDef {
      TRANSACTION
      API
      PAGESPEED
    }
  }
`;

export interface AlertExtraDataResponse {
  alertData: AlertExtraData;
}

export const GQL_ALERT_EXTRA_DATA = gql`
  query alertData($id: Int!) {
    alertData(id: $id) {
      id
      data {
        version
        console {
          message
          line
          file
          type
        }
        results {
          step
          step_def
          title
          values
          start_time
          response_time
          result
          verbose
          error
          sysstat
          details {
            status_code
            response_headers
            request_headers
            response_proto
            response_proto_major
            response_content_type
            response_body
            url
            cookies
          }
        }
        network
        pagespeed
      }
      picture
      alert {
        id
        created_at
        monitoring_server {
          id
          name
          location
          address_ipv4
          address_ipv6
        }
        created_at
        output
      }
      gphttp
    }
  }
`;

export interface GlobalAlertsResponse {
  globalAlertStates: GlobalAlertStatesResponse;
}

export const GQL_GLOBAL_ALERT_STATES = gql`
  query alerts(
    $offset: Int
    $limit: Int
    $sort: String
    $show_deleted: String
    $service_id: Int
    $search: String
    $filters: AlertsFilters
    $visible_range: AlertsFiltersRange
  ) {
    globalAlertStates: alerts(
      offset: $offset
      limit: $limit
      sort: $sort
      show_deleted: $show_deleted
      service_id: $service_id
      search: $search
      filters: $filters
      visible_range: $visible_range
    ) {
      stats {
        resolved
        down
        ignored
        sms_and_phone_alerts_used
        sms_and_phone_alerts_available
      }
      items {
        ...AlertListItemFields
      }
      total
    }
  }
`;

export const GQL_GLOBAL_ALERT_STATE_TOGGLE_IGNORE = gql`
  mutation globalAlertStateToggleIgnore($id: Int!) {
    response: globalAlertStateToggleIgnore(id: $id) {
      ... on BaseResponse {
        status
      }
      ... on ErrorResponse {
        status
        message
      }
    }
  }
`;

export const GQL_GLOBAL_ALERT_STATE_BULK_SET_IGNORE = gql`
  mutation globalAlertStateBulkSetIgnore($ids: [Int!]!, $ignored: Boolean!) {
    response: globalAlertStateBulkSetIgnore(ids: $ids, ignored: $ignored) {
      ... on BaseResponse {
        status
      }
      ... on ErrorResponse {
        status
        message
      }
    }
  }
`;

export interface AlertFilteringDataResult {
  monitoringGroups: MonitoringGroup[];
  services: ServiceItems;
  tags: ServiceTag[];
  contactGroups: ContactGroupBaseItems;
}

export const GQL_MONITORING_GROUPS_AND_TAGS_FILTER_DATA = gql`
  query Services {
    monitoringGroups {
      id
      location
    }
    tags {
      id
      tag
      color_hex
    }
    contactGroups(limit: 1000, offset: 0) {
      items {
        id
        name
      }
    }
  }
`;

export const GQL_ALERTING_FILTER_DATA_SERVICES = gql`
  query ListAlerts($search: String, $limit: Int) {
    values: services(search: $search, limit: $limit) {
      total
      items {
        key: id
        display: name
      }
    }
  }
`;
